import React from 'react';
import type { DifficultyLevel } from '../../types/index.js';
import {
  DIFFICULTY_KIND_LABEL,
  DIFFICULTY_UNRATED_LABEL,
  difficultyAria,
  difficultyLevelInfo
} from '../../utils/difficulty.js';
import { toPersianDigits } from '../../utils/timeUtils.js';

export interface DifficultyMeterProps {
  kind: 'rhythm' | 'lead';
  value: DifficultyLevel | null;
  /** Small variant for lists (same as `size="sm"`). */
  compact?: boolean;
  size?: 'sm' | 'md';
  /** Append «· متوسط» (DLG-12 / admin). */
  showLevelName?: boolean;
  className?: string;
}

/** FRE-188: label, five pips (filled from the inline start) and the Persian digit. */
export const DifficultyMeter: React.FC<DifficultyMeterProps> = ({
  kind,
  value,
  compact = false,
  size,
  showLevelName = false,
  className = ''
}) => {
  const resolved = size ?? (compact ? 'sm' : 'md');
  const info = difficultyLevelInfo(value);
  const level = info?.value ?? 0;
  const pip = resolved === 'sm' ? 'size-1.5' : 'size-2';
  return (
    <span
      role="img"
      aria-label={difficultyAria(kind, value)}
      data-testid={`difficulty-meter-${kind}`}
      data-value={info ? String(info.value) : 'none'}
      className={`inline-flex items-center gap-1.5 ${className}`}
    >
      <span aria-hidden="true" className="text-xs text-base-content/60">
        {DIFFICULTY_KIND_LABEL[kind]}
      </span>
      <span aria-hidden="true" className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            data-filled={n <= level ? 'true' : 'false'}
            className={`${pip} rounded-full ${n <= level ? 'bg-accent' : 'bg-base-content/20'}`}
          />
        ))}
      </span>
      <span
        aria-hidden="true"
        className={`text-xs ${info ? 'font-semibold text-base-content/80' : 'text-base-content/60'}`}
      >
        {info ? toPersianDigits(info.value) : DIFFICULTY_UNRATED_LABEL}
        {info && showLevelName ? ` · ${info.label}` : ''}
      </span>
    </span>
  );
};
DifficultyMeter.displayName = 'DifficultyMeter';

export interface DifficultySummaryProps {
  rhythm: DifficultyLevel | null | undefined;
  lead: DifficultyLevel | null | undefined;
  size?: 'sm' | 'md';
  showLevelName?: boolean;
  /** Viewer chip: a button that opens DLG-12. */
  interactive?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Rhythm meter + `·` + lead meter. Static: renders nothing when both are unrated (AC-56). */
export const DifficultySummary: React.FC<DifficultySummaryProps> = ({
  rhythm,
  lead,
  size = 'sm',
  showLevelName = false,
  interactive = false,
  onClick,
  className = ''
}) => {
  const r = rhythm ?? null;
  const l = lead ?? null;
  if (!interactive && r === null && l === null) return null;
  const inner = (
    <>
      <DifficultyMeter kind="rhythm" value={r} size={size} showLevelName={showLevelName} />
      <span aria-hidden="true" className="text-base-content/40">·</span>
      <DifficultyMeter kind="lead" value={l} size={size} showLevelName={showLevelName} />
    </>
  );
  if (!interactive) {
    return <span className={`inline-flex items-center gap-2 ${className}`}>{inner}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      data-testid="difficulty-chip"
      className={`relative inline-flex items-center gap-2 h-8 px-2 rounded-lg hover:bg-base-300/60 focus-visible:outline-none focus-visible:ring-2 ring-primary before:absolute before:-inset-y-1.5 before:inset-x-0 before:content-[''] ${className}`}
    >
      {inner}
    </button>
  );
};
DifficultySummary.displayName = 'DifficultySummary';
